import { useEffect } from 'react';
import { useStore } from '@nanostores/react';
import {
  Badge,
  Banner,
  Button,
  Card,
  Divider,
  HStack,
  Heading,
  Link,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  VStack,
} from '@astryxdesign/core';
import { useSearchParams } from 'react-router';

import { PlanCards } from '../components/PlanCards';
import { formatDate, formatDateTime, formatPrice } from '../lib/api';
import { $auth } from '../stores/auth';
import {
  $billingError,
  $billingLoading,
  $billingNotice,
  $billingStatus,
  $graceEndsAt,
  $invoices,
  $pendingPlan,
  $portalAvailable,
  $portalPending,
  $provider,
  $subHistory,
  $subscription,
  $switching,
  $undoPending,
  cancelScheduledChange,
  loadBilling,
  openPortal,
  switchPlan,
} from '../stores/billing';
import { $plans, loadPlans } from '../stores/plans';

export function BillingPage() {
  const auth = useStore($auth);
  const plans = useStore($plans);
  const subscription = useStore($subscription);
  const pendingPlan = useStore($pendingPlan);
  const history = useStore($subHistory);
  const invoices = useStore($invoices);
  const billingStatus = useStore($billingStatus);
  const graceEndsAt = useStore($graceEndsAt);
  const provider = useStore($provider);
  const portalAvailable = useStore($portalAvailable);
  const portalPending = useStore($portalPending);
  const loading = useStore($billingLoading);
  const error = useStore($billingError);
  const notice = useStore($billingNotice);
  const switching = useStore($switching);
  const undoPending = useStore($undoPending);

  // Stripe sends the browser back here with ?checkout=success|cancelled.
  const [params, setParams] = useSearchParams();
  const checkout = params.get('checkout');

  useEffect(() => {
    void loadBilling();
    void loadPlans();
  }, []);

  // The webhook applies the plan, and it can land a moment after the redirect,
  // so re-read once shortly after a successful checkout.
  useEffect(() => {
    if (checkout !== 'success') return;
    const timer = window.setTimeout(() => void loadBilling(), 2500);
    return () => window.clearTimeout(timer);
  }, [checkout]);

  function dismissCheckoutBanner() {
    const next = new URLSearchParams(params);
    next.delete('checkout');
    setParams(next, { replace: true });
  }

  if (loading && !subscription && plans.length === 0) {
    return <Spinner size="lg" label="Loading billing" />;
  }

  const pastDue = billingStatus === 'past_due' || billingStatus === 'unpaid';

  return (
    <VStack gap={3}>
      <VStack gap={0.5}>
        <Heading level={1}>Plan &amp; billing</Heading>
        <Text color="secondary">
          Upgrades take effect immediately and are prorated. Downgrades wait for your renewal date.
        </Text>
      </VStack>

      {checkout === 'success' && (
        <Banner
          status="success"
          title="Payment received"
          description="Your subscription is being activated. This page refreshes itself once the payment provider confirms it — usually within a few seconds."
          isDismissable
          onDismiss={dismissCheckoutBanner}
        />
      )}
      {checkout === 'cancelled' && (
        <Banner
          status="info"
          title="Checkout cancelled"
          description="Nothing was charged and your plan has not changed."
          isDismissable
          onDismiss={dismissCheckoutBanner}
        />
      )}

      {error && <Banner status="error" title="Could not change plan" description={error} />}
      {notice && <Banner status="success" title="Plan updated" description={notice} />}

      {pastDue && (
        <Banner
          status="error"
          title="Your last payment failed"
          description={
            graceEndsAt
              ? `Update your card to keep your current limits. On ${formatDate(graceEndsAt)} the account drops to free-tier limits — your API keys keep working, just slower.`
              : 'Update your card to keep your current limits. Your API keys keep working in the meantime.'
          }
          endContent={
            portalAvailable ? (
              <Button
                label="Update payment method"
                variant="primary"
                size="sm"
                isLoading={portalPending}
                onClick={() => void openPortal()}
              />
            ) : undefined
          }
        />
      )}

      {pendingPlan && (
        <Banner
          status="info"
          title={`Scheduled change to ${pendingPlan.name}`}
          description={
            subscription
              ? `Takes effect ${formatDate(subscription.period_end)}. Until then you keep your current limits.`
              : 'Takes effect at the end of your current billing period.'
          }
          endContent={<KeepCurrentPlanButton isLoading={undoPending} />}
        />
      )}

      {subscription?.cancel_at_period_end && !pendingPlan && (
        <Banner
          status="warning"
          title="Subscription ends at the period end"
          description={`Your paid plan runs until ${formatDate(subscription.period_end)}, then the account moves to Free. Resubscribe any time before then to keep it.`}
          endContent={<KeepCurrentPlanButton isLoading={undoPending} />}
        />
      )}

      {provider === 'manual' && (
        <Banner
          status="info"
          title="No payment provider configured"
          description="This deployment records plan changes directly (provider “manual”) and charges nothing. Set BILLING_PROVIDER=stripe with STRIPE_SECRET_KEY, STRIPE_WEBHOOK_SECRET and STRIPE_PRICE_<PLAN> to take real payments."
        />
      )}

      {auth.plan && (
        <Card padding={3} variant="default">
          <VStack gap={1.5}>
            <HStack gap={2} justify="between" vAlign="center" wrap="wrap">
              <Text weight="semibold">Current subscription</Text>
              {portalAvailable && (
                <Button
                  label="Manage billing"
                  variant="secondary"
                  size="sm"
                  isLoading={portalPending}
                  onClick={() => void openPortal()}
                />
              )}
            </HStack>

            <VStack gap={1}>
              <Row
                label="Plan"
                value={auth.plan.name}
                badge={
                  pastDue ? (
                    <Badge label="Past due" variant="error" />
                  ) : (
                    <Badge label="Active" variant="success" />
                  )
                }
              />
              <Row
                label="Price"
                value={
                  auth.plan.contact_sales
                    ? 'Custom'
                    : `${formatPrice(auth.plan.price_cents, auth.plan.currency)} / ${auth.plan.interval}`
                }
              />
              {subscription && (
                <>
                  <Row label="Current period" value={subscription.current_period} />
                  <Row
                    label={subscription.cancel_at_period_end ? 'Ends' : 'Renews'}
                    value={formatDate(subscription.period_end)}
                  />
                  <Row label="Provider" value={subscription.provider} />
                </>
              )}
            </VStack>

            {portalAvailable && (
              <>
                <Divider variant="subtle" />
                <Text size="sm" color="secondary">
                  Cards, receipts and cancellation are handled on the provider&apos;s hosted page — this
                  service never sees your payment details.
                </Text>
              </>
            )}
          </VStack>
        </Card>
      )}

      <VStack gap={2}>
        <Heading level={2}>Change plan</Heading>
        <PlanCards
          plans={plans}
          currentCode={auth.plan?.code}
          pendingCode={switching}
          scheduledCode={pendingPlan?.code}
          onSelect={(plan) => void switchPlan(plan.code)}
        />
      </VStack>

      {invoices.length > 0 && (
        <Card padding={3} variant="default">
          <VStack gap={2}>
            <Text weight="semibold">Invoices</Text>
            <Table density="balanced" dividers="rows" hasHover>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>Date</TableHeaderCell>
                  <TableHeaderCell>Number</TableHeaderCell>
                  <TableHeaderCell>Plan</TableHeaderCell>
                  <TableHeaderCell>Amount</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                  <TableHeaderCell> </TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((inv) => (
                  <TableRow key={inv.id}>
                    <TableCell>
                      <Text size="sm" color="secondary">
                        {formatDate(inv.issued_at)}
                      </Text>
                    </TableCell>
                    <TableCell>
                      <span className="mono">{inv.number ?? '—'}</span>
                    </TableCell>
                    <TableCell>
                      <Text size="sm">{inv.plan_code ?? '—'}</Text>
                    </TableCell>
                    <TableCell>{formatPrice(inv.amount_paid || inv.amount_due, inv.currency)}</TableCell>
                    <TableCell>
                      <Badge
                        label={inv.status}
                        variant={
                          inv.status === 'paid'
                            ? 'success'
                            : inv.status === 'open'
                              ? 'warning'
                              : 'neutral'
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {inv.pdf_url ? (
                        <Link href={inv.pdf_url} isExternalLink>
                          PDF
                        </Link>
                      ) : inv.hosted_url ? (
                        <Link href={inv.hosted_url} isExternalLink>
                          View
                        </Link>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </VStack>
        </Card>
      )}

      {history.length > 0 && (
        <Card padding={3} variant="default">
          <VStack gap={2}>
            <Text weight="semibold">Subscription history</Text>
            <Table density="balanced" dividers="rows">
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>Plan</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                  <TableHeaderCell>Price</TableHeaderCell>
                  <TableHeaderCell>Period</TableHeaderCell>
                  <TableHeaderCell>Started</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell>
                      <Text size="sm" weight="medium">
                        {s.plan_code}
                      </Text>
                    </TableCell>
                    <TableCell>
                      <Badge
                        label={s.status}
                        variant={
                          s.status === 'active'
                            ? 'success'
                            : s.status === 'past_due' || s.status === 'unpaid'
                              ? 'error'
                              : 'neutral'
                        }
                      />
                    </TableCell>
                    <TableCell>{formatPrice(s.price_cents, s.currency)}</TableCell>
                    <TableCell>
                      <span className="mono">{s.current_period}</span>
                    </TableCell>
                    <TableCell>
                      <Text size="sm" color="secondary">
                        {formatDateTime(s.created_at)}
                      </Text>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </VStack>
        </Card>
      )}
    </VStack>
  );
}

// Undoing is one click from the same banner that announced the change, because
// that banner is where a customer who changed their mind goes looking.
function KeepCurrentPlanButton({ isLoading }: { isLoading: boolean }) {
  return (
    <Button
      label="Keep current plan"
      variant="secondary"
      size="sm"
      isLoading={isLoading}
      onClick={() => void cancelScheduledChange()}
    />
  );
}

function Row({
  label,
  value,
  badge,
}: {
  label: string;
  value: string;
  badge?: React.ReactNode;
}) {
  return (
    <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between', alignItems: 'center' }}>
      <Text size="sm" color="secondary">
        {label}
      </Text>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <Text size="sm" weight="medium">
          {value}
        </Text>
        {badge}
      </div>
    </div>
  );
}
